import Card from "components/card";
import React, { useEffect, useMemo, useState } from "react";
import { RiErrorWarningFill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import ContainerNotes from "./ContainerNotes";
import SelectField from "components/fields/SelectField";
import Modal from "components/modal";
import Button from "components/button";
import LoaderButton from "components/button/LoaderButton";
import { changeContainerStatus } from "store/containerSlice";
import { showToast } from "components/toast";

function BasicInfo({ refreshList }) {
  const dispatch = useDispatch();
  const { container: containerPharm = {} } = useSelector(
    (state) => state.auth.permissions || {}
  );

  const {
    containerDetailsData,
    containerDetailsLoading,
    containerDetailsError,
    containerStatusData,
    containerStatusLoading,
    containerStatusError,
    containerChangeStatusLoading,
    containerChangeStatusError,
  } = useSelector((state) => state.containers);

  const [pendingStatus, setPendingStatus] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const statusOptions = useMemo(
    () =>
      containerStatusData?.map((status) => ({
        label: status.name,
        value: status._id,
      })) || [],
    [containerStatusData]
  );

  // Prepare details in a consistent structure
  const containerDetails = useMemo(
    () => [
      {
        label: "Container No",
        value: containerDetailsData?.containerNo || "N/A",
      },
      {
        label: "Departure Port",
        value: containerDetailsData?.departurePort || "N/A",
      },
      {
        label: "Arrival Port",
        value: containerDetailsData?.arrivalPort || "N/A",
      },
      {
        label: "Status",
        value: containerDetailsData?.status?._id || "N/A",
        isStatus: true,
      },
      {
        label: "Departure Date",
        value: containerDetailsData.departureTimeDate || "N/A",
      },
      {
        label: "ETA",
        value: containerDetailsData?.eta || "N/A",
      },
      {
        label: "Created At",
        value: containerDetailsData?.createdAt
          ? new Date(containerDetailsData.createdAt).toLocaleDateString(
              "en-US",
              {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }
            )
          : "N/A",
      },
      {
        label: "Updated At",
        value: containerDetailsData?.updatedAt
          ? new Date(containerDetailsData.updatedAt).toLocaleDateString(
              "en-US",
              {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }
            )
          : "N/A",
      },
    ],
    [containerDetailsData]
  );

  const handleConfirmChange = async () => {
    if (!pendingStatus?.value || !containerDetailsData?._id) return;

    try {
      const res = await dispatch(
        changeContainerStatus({
          containerId: containerDetailsData?._id,
          status: pendingStatus?.value,
        })
      ).unwrap();

      if (res?.statuscode === 200) {
        refreshList();
        showToast.success(`Container ${pendingStatus?.label} successfully!`);

        setIsConfirmModalOpen(false);
        setPendingStatus(null);
      } else {
        console.error(
          "Order status change failed:",
          res?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Order status change failed:", error);
    }
  };

  return (
    <Card extra={"w-full h-full p-3"}>
      {/* Header */}

      {/* Grid of details */}
      <div className="grid grid-cols-1 gap-2 px-2 md:grid-cols-12">
        {/* First div (9 columns on md+) */}
        <div className=" md:col-span-7">
          <div className="mb-4 mt-2 flex w-full items-center justify-between">
            <h4 className="px-2 text-xl font-bold text-navy-700 dark:text-white">
              Container Details
            </h4>
          </div>
          <div className="grid grid-cols-2 gap-2 px-2 md:grid-cols-3">
            {containerDetails?.map((item, index) => (
              <div
                key={index}
                className="flex flex-col items-start justify-center rounded-2xl bg-white bg-clip-border px-3 py-4 shadow-3xl shadow-shadow-500 dark:!bg-navy-700 dark:shadow-none"
              >
                <p className="text-sm text-gray-600">{item.label}</p>

                {item.isStatus ? (
                  // ✅ Status field logic
                  !containerDetailsLoading && !containerStatusLoading ? (
                    <SelectField
                      options={statusOptions}
                      className="w-full"
                      placeholder="Select Status"
                      disabled={!containerPharm?.statusChange}
                      error={containerStatusError}
                      value={
                        item.value
                          ? statusOptions.find(
                              (opt) => opt.value === item.value
                            )
                          : null
                      }
                      onChange={(selected) => {
                        setPendingStatus(selected);
                        setIsConfirmModalOpen(true);
                      }}
                    />
                  ) : (
                    // Skeleton while either detail/status is loading
                    <div className="mt-1 h-5 w-20 animate-pulse rounded-md bg-gray-100 dark:bg-navy-700"></div>
                  )
                ) : containerDetailsLoading ? (
                  // ✅ Skeleton loader for non-status fields
                  <div className="mt-1 h-5 w-10 animate-pulse rounded-md bg-gray-100 dark:bg-navy-700"></div>
                ) : containerDetailsError ? (
                  // ✅ Error state
                  <div className="flex items-center gap-2 text-red-500">
                    <RiErrorWarningFill className="h-4 w-4" />
                  </div>
                ) : (
                  // ✅ Normal text
                  <p className="text-base font-medium text-navy-700 dark:text-white">
                    {item.value}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Second div (3 columns on md+) */}
        <div className=" md:col-span-5">
          <ContainerNotes />
        </div>
      </div>
      {/* Confirm Modal */}

      <Modal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        className="max-w-md"
      >
        <h2 className="border-b py-3 pl-2 text-lg font-bold text-navy-700 dark:text-white">
          Confirm Status Change
        </h2>
        <div className="p-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Are you sure you want to change the order status to{" "}
            <span className="font-semibold">{pendingStatus?.label}</span>?
          </p>
          {containerChangeStatusError && (
            <div className="mt-2">
              <p className="text-sm text-red-500">
                {containerChangeStatusError}
              </p>
            </div>
          )}
          <div className="mt-6 flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsConfirmModalOpen(false)}
            >
              Cancel
            </Button>
            <LoaderButton
              isLoading={containerChangeStatusLoading}
              onClick={handleConfirmChange}
            >
              Confirm
            </LoaderButton>
          </div>
        </div>
      </Modal>
    </Card>
  );
}

export default BasicInfo;
